'use client';
import React, { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useLocale } from 'use-intl';
import Image from '../../../image';
import SectionTitle from './SectionTitle';
import SectionYearSelector from './SectionYearSelector';
import styles from './NewsSection.module.css';
import { fetchConferenceNewsByYear } from '../api';
import {
  formatConferenceNewsPublishDate,
  getConferenceNewsTags,
  getConferenceNewsText,
  normalizeConferenceNewsData,
} from '../news.helpers';
import type { ConferenceNewsListResp, ConferenceSite } from '../types';

interface NewsSectionProps {
  data?: ConferenceNewsListResp;
  title?: string;
  site?: ConferenceSite;
  years?: number[];
}

function NewsPagerArrow({
  direction,
}: {
  direction: 'left' | 'right';
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      aria-hidden="true"
      style={
        direction === 'left'
          ? { transform: 'rotate(180deg)' }
          : undefined
      }
    >
      <circle
        cx="20"
        cy="20"
        r="19.4"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path
        d="M18 16L22 20L18 24"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}

const chunkNewsList = <T,>(list: T[], size: number) => {
  const pages: T[][] = [];
  for (let index = 0; index < list.length; index += size) {
    pages.push(list.slice(index, index + size));
  }
  return pages;
};

const MOBILE_PAGER_TRACK_WIDTH_REM = 1.5;

/**
 * 大会资讯组件
 */
export default function NewsSection({
  data,
  title,
  site,
  years,
}: NewsSectionProps) {
  const locale = useLocale() === 'zh' ? 'zh' : 'en';
  const isOverseaSite = site === 'en';
  const [isPending, startTransition] = useTransition();
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const desktopPageTimerRef = useRef<number | null>(null);
  const desktopFadeTimerRef = useRef<number | null>(null);
  const requestIdRef = useRef(0);
  const [newsData, setNewsData] = useState(() =>
    normalizeConferenceNewsData(data)
  );
  const [desktopPage, setDesktopPage] = useState(1);
  const [isDesktopFading, setIsDesktopFading] = useState(false);
  const [mobileScrollLeft, setMobileScrollLeft] = useState(0);
  const [selectedYear, setSelectedYear] = useState(() => years?.[0] || data?.conferenceYear || new Date().getFullYear());

  const resetPagerState = () => {
    setDesktopPage(1);
    setIsDesktopFading(false);
    setMobileScrollLeft(0);
    if (desktopPageTimerRef.current !== null) {
      window.clearTimeout(desktopPageTimerRef.current);
      desktopPageTimerRef.current = null;
    }
    if (desktopFadeTimerRef.current !== null) {
      window.clearTimeout(desktopFadeTimerRef.current);
      desktopFadeTimerRef.current = null;
    }
    mobileScrollRef.current?.scrollTo({ left: 0 });
  };

  useEffect(() => {
    setNewsData(normalizeConferenceNewsData(data));
    if (years?.[0]) {
      setSelectedYear(years[0]);
    }
    resetPagerState();
  }, [data, years]);

  useEffect(
    () => () => {
      if (desktopPageTimerRef.current !== null) {
        window.clearTimeout(desktopPageTimerRef.current);
        desktopPageTimerRef.current = null;
      }
      if (desktopFadeTimerRef.current !== null) {
        window.clearTimeout(desktopFadeTimerRef.current);
        desktopFadeTimerRef.current = null;
      }
    },
    []
  );

  const list = useMemo(() => newsData.list || [], [newsData.list]);
  const total = newsData.total || 0;
  const pageSize = 3;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const desktopList = useMemo(
    () => list.slice((desktopPage - 1) * pageSize, desktopPage * pageSize),
    [desktopPage, list]
  );
  const mobilePages = useMemo(() => chunkNewsList(list, pageSize), [list]);
  const yearOptions = useMemo(() => years || [], [years]);

  const handleOpenNews = (targetURL?: string) => {
    if (!targetURL) {
      return;
    }
    window.open(targetURL, '_blank', 'noopener,noreferrer');
  };

  const handleDesktopPageChange = (nextPage: number) => {
    if (
      isDesktopFading ||
      nextPage === desktopPage ||
      nextPage < 1 ||
      nextPage > pageCount
    ) {
      return;
    }

    setIsDesktopFading(true);
    if (desktopPageTimerRef.current !== null) {
      window.clearTimeout(desktopPageTimerRef.current);
    }
    if (desktopFadeTimerRef.current !== null) {
      window.clearTimeout(desktopFadeTimerRef.current);
    }
    desktopPageTimerRef.current = window.setTimeout(() => {
      setDesktopPage(nextPage);
      desktopPageTimerRef.current = null;
      desktopFadeTimerRef.current = window.setTimeout(() => {
        setIsDesktopFading(false);
        desktopFadeTimerRef.current = null;
      }, 50);
    }, 300);
  };

  const handleYearChange = (year: number) => {
    if (year === selectedYear) {
      return;
    }
    setSelectedYear(year);
    resetPagerState();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    startTransition(() => {
      void fetchConferenceNewsByYear({
        conferenceYear: year,
        site,
      }).then((nextData) => {
        if (requestIdRef.current === requestId) {
          setNewsData(normalizeConferenceNewsData(nextData));
        }
      });
    });
  };

  const getMobileIndicatorLeft = () => {
    const scrollNode = mobileScrollRef.current;
    if (!scrollNode) {
      return 0;
    }

    const scrollableWidth = scrollNode.scrollWidth - scrollNode.clientWidth;
    if (scrollableWidth <= 0) {
      return -1;
    }

    const indicatorWidthRem =
      MOBILE_PAGER_TRACK_WIDTH_REM / mobilePages.length;
    const maxOffsetRem = MOBILE_PAGER_TRACK_WIDTH_REM - indicatorWidthRem;
    const ratio = Math.min(1, Math.max(0, mobileScrollLeft / scrollableWidth));
    return ratio * maxOffsetRem;
  };

  const handleMobileScroll = () => {
    if (!mobileScrollRef.current) {
      return;
    }
    setMobileScrollLeft(mobileScrollRef.current.scrollLeft);
  };

  const mobileIndicatorLeft = getMobileIndicatorLeft();

  return (
    <section id="news" className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <SectionTitle
            className={locale === 'en' ? styles.englishTitle : ''}
          >
            {title}
          </SectionTitle>
          <SectionYearSelector
            selectedYear={selectedYear}
            years={yearOptions}
            onChange={handleYearChange}
          />
        </div>
        {list.length ? (
          <>
            <div
              className={`${styles.desktopList} ${
                isDesktopFading ? styles.desktopListFading : ''
              } ${pageCount > 1 ? styles.desktopListPaged : ''}`}
            >
              {desktopList.map((item) => {
                const titleText = getConferenceNewsText(item, 'title', locale) || '-';
                const summaryText =
                  getConferenceNewsText(item, 'summary', locale) || '-';
                const copyBlockClassName = isOverseaSite
                  ? `${styles.copyBlock} ${styles.copyBlockOversea}`
                  : styles.copyBlock;
                const cardTitleClassName = isOverseaSite
                  ? `${styles.cardTitle} ${styles.cardTitleOversea}`
                  : styles.cardTitle;
                const tags = getConferenceNewsTags(item, locale);
                const showTags = locale === 'zh' && tags.length > 0;
                const publishDateText = formatConferenceNewsPublishDate(
                  item.publishTime
                );

                return (
                  <button
                    key={item.id || `${item.targetURL}-${titleText}`}
                    className={styles.card}
                    onClick={() => handleOpenNews(item.targetURL)}
                    type="button"
                  >
                    <div className={styles.cardContent}>
                      <div className={styles.textWrap}>
                        <div className={copyBlockClassName}>
                          <h3 className={cardTitleClassName} title={titleText}>
                            {titleText}
                          </h3>
                          <p className={styles.cardSummary} title={summaryText}>
                            {summaryText}
                          </p>
                          {showTags ? (
                            <div className={styles.tags}>
                              {tags.map((tag, tagIndex) => (
                                <span
                                  key={`${item.id || item.targetURL || titleText}-tag-${tagIndex}`}
                                  className={styles.tag}
                                  title={tag}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        <p className={styles.publishTime}>{publishDateText}</p>
                      </div>
                      <div className={styles.imageWrap}>
                        {item.coverImage ? (
                          <Image
                            src={item.coverImage}
                            alt={titleText}
                            className={styles.coverImage}
                            loading="lazy"
                          />
                        ) : (
                          <div className={styles.imagePlaceholder}>-</div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div
              ref={mobileScrollRef}
              className={styles.mobileList}
              onScroll={handleMobileScroll}
            >
              {mobilePages.map((pageItems, pageIndex) => (
                <div key={`mobile-page-${pageIndex + 1}`} className={styles.mobilePage}>
                  {pageItems.map((item, itemIndex) => {
                    const titleText =
                      getConferenceNewsText(item, 'title', locale) || '-';
                    const summaryText =
                      getConferenceNewsText(item, 'summary', locale) || '-';
                    const publishDateText = formatConferenceNewsPublishDate(
                      item.publishTime
                    );

                    return (
                      <button
                        key={`mobile-${item.id || `${pageIndex}-${itemIndex}`}`}
                        className={styles.mobileItem}
                        onClick={() => handleOpenNews(item.targetURL)}
                        type="button"
                      >
                        <div className={styles.mobileCopy}>
                          <h3
                            className={
                              isOverseaSite
                                ? `${styles.mobileTitle} ${styles.mobileTitleOversea}`
                                : styles.mobileTitle
                            }
                            title={titleText}
                          >
                            {titleText}
                          </h3>
                          <p
                            className={
                              isOverseaSite
                                ? `${styles.mobileSummary} ${styles.mobileSummaryOversea}`
                                : styles.mobileSummary
                            }
                            title={summaryText}
                          >
                            {summaryText}
                          </p>
                        </div>
                        <p
                          className={
                            isOverseaSite
                              ? `${styles.mobileDate} ${styles.mobileDateOversea}`
                              : styles.mobileDate
                          }
                        >
                          {publishDateText}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {pageCount > 1 ? (
              <div
                className={styles.desktopPager}
                aria-label={locale === 'zh' ? '大会资讯翻页' : 'News pagination'}
              >
                <button
                  className={styles.pageButton}
                  disabled={desktopPage === 1}
                  onClick={() => handleDesktopPageChange(desktopPage - 1)}
                  type="button"
                  aria-label={locale === 'zh' ? '上一页' : 'Previous page'}
                >
                  <NewsPagerArrow direction="left" />
                </button>
                <span className={styles.pageText}>
                  {desktopPage}/{pageCount}
                </span>
                <button
                  className={styles.pageButton}
                  disabled={desktopPage === pageCount}
                  onClick={() => handleDesktopPageChange(desktopPage + 1)}
                  type="button"
                  aria-label={locale === 'zh' ? '下一页' : 'Next page'}
                >
                  <NewsPagerArrow direction="right" />
                </button>
              </div>
            ) : null}

            {mobilePages.length > 1 && mobileIndicatorLeft >= 0 ? (
              <div className={styles.mobilePager} aria-hidden="true">
                <div className={styles.mobilePagerTrack}>
                  <span
                    className={styles.mobilePagerIndicator}
                    style={{
                      left: `${mobileIndicatorLeft}rem`,
                      width: `${MOBILE_PAGER_TRACK_WIDTH_REM / mobilePages.length}rem`,
                    }}
                  />
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
